import React, { useState, useEffect, useContext } from "react";
import { Container, Row, Col, Spinner } from "reactstrap";
import { useLocation, useNavigate } from "react-router-dom";
import { FaThLarge, FaUtensils, FaTheaterMasks, FaConciergeBell, FaShoppingBag } from "react-icons/fa";
import "./deals.css";
import DealCard from "../../../components/Cards/DealCard";
import { AuthContext, AuthContextType } from "../../../contexts/auth-context";
import { getDeals, Deal } from "../../../actions/deals-actions";

// Categories must match the exact values stored in the database
const CATEGORIES = [
  { key: "all", label: "All", icon: FaThLarge },
  { key: "Restaurant", label: "Food", icon: FaUtensils },
  { key: "Entertainment", label: "Fun", icon: FaTheaterMasks },
  { key: "Service", label: "Services", icon: FaConciergeBell },
  { key: "Shopping", label: "Shop", icon: FaShoppingBag },
];

function Deals() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useContext<AuthContextType>(AuthContext);
  const [deals, setDeals] = useState<Deal[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Get category from URL query params (preserve original case)
  const urlParams = new URLSearchParams(location.search);
  const categoryFromUrl = urlParams.get("category") || "all";
  const [activeCategory, setActiveCategory] = useState(categoryFromUrl);

  // Update activeCategory when URL changes
  useEffect(() => {
    const urlParams = new URLSearchParams(location.search);
    const cat = urlParams.get("category") || "all";
    setActiveCategory(cat);
  }, [location.search]);

  useEffect(() => {
    const fetchDeals = async () => {
      setIsLoading(true);
      setError(null);
      try {
        const response = await getDeals(user?.partnerId);
        setDeals(response.data);
      } catch (err: any) {
        setError(err.response?.data || "Failed to load deals");
        console.error("Error fetching deals:", err);
      } finally {
        setIsLoading(false);
      }
    };

    if (user?.partnerId) {
      fetchDeals();
    }
  }, [user?.partnerId]);

  const handleCategoryChange = (category: string) => {
    setActiveCategory(category);
    if (category === "all") {
      navigate("/admin/deals");
    } else {
      navigate(`/admin/deals?category=${category}`);
    }
  };

  const filteredDeals = activeCategory.toLowerCase() === "all"
    ? deals
    : deals.filter(deal => deal.category.toLowerCase() === activeCategory.toLowerCase());

  if (isLoading) {
    return (
      <Container className="mt-3 pb-4" fluid>
        <div className="text-center py-5">
          <Spinner color="primary" />
          <p className="mt-2 text-muted">Loading deals...</p>
        </div>
      </Container>
    );
  }

  if (error) {
    return (
      <Container className="mt-3 pb-4" fluid>
        <div className="text-center py-5">
          <p className="text-danger">{error}</p>
        </div>
      </Container>
    );
  }

  return (
    <Container className="mt-3 pb-4" fluid>
      {/* Category Filter - Icon Grid */}
      <div
        className="mb-4 d-flex justify-content-between"
        style={{
          backgroundColor: "#f8f9fa",
          borderRadius: "12px",
          padding: "8px"
        }}
      >
        {CATEGORIES.map((cat) => {
          const isActive = activeCategory.toLowerCase() === cat.key.toLowerCase();
          const IconComponent = cat.icon;

          return (
            <button
              key={cat.key}
              onClick={() => handleCategoryChange(cat.key)}
              style={{
                flex: 1,
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                gap: "4px",
                padding: "10px 4px",
                border: "none",
                borderRadius: "10px",
                backgroundColor: isActive ? "#343a40" : "transparent",
                color: isActive ? "#fff" : "#6c757d",
                cursor: "pointer",
                transition: "all 0.2s ease"
              }}
            >
              <IconComponent size={18} />
              <span style={{ fontSize: "11px", fontWeight: isActive ? "600" : "500" }}>
                {cat.label}
              </span>
            </button>
          );
        })}
      </div>

      {filteredDeals.length === 0 ? (
        <div className="text-center py-5">
          <p className="text-muted">No deals available in this category</p>
        </div>
      ) : (
        <Row>
          {filteredDeals.map((deal) => (
            <Col key={deal.id} xs="6" sm="6" md="4" lg="3" className="mb-3">
              <DealCard
                deal={{
                  id: deal.id,
                  title: deal.title,
                  priceFrom: deal.priceFrom,
                  priceNew: deal.priceActual,
                  photoUrl: deal.photoUrl || "/placeholder-deal.jpg",
                  dealUrl: `/admin/deal/${deal.id}`,
                  category: deal.category
                }}
              />
            </Col>
          ))}
        </Row>
      )}
    </Container>
  );
}

export default Deals;
